<template>
    <card-normal v-if="contact">
        <flex-container>
            <flex-container :size="5">
                <text-heading>
                    Contactgegevens
                </text-heading>
            </flex-container>

            <flex-container :size="5" justify-content="flex-end">
                <button-outline @click="openContactEditModal">
                    Wijzig
                </button-outline>
            </flex-container>
        </flex-container>

        <inline-tabs selected-name="contact">
            <template #labels>
                <inline-tab-label name="contact" @click="contactTab = 'contact'">
                    Contactpersoon
                </inline-tab-label>

                <inline-tab-label
                    v-if="hasSecondaryContact"
                    name="existing-partner"
                    @click="contactTab = 'partner'"
                >
                    {{ contact.secondaryContact?.name.fullName || 'Partner' }}
                </inline-tab-label>

                <button-text
                    v-if="!hasSecondaryContact"
                    style="align-self: center;"
                    @click="openContactEditModal"
                >
                    <svg-icon :icon="IconAdd" />
                    <span class="contact-card-partner--responsive">Partner toevoegen</span>
                </button-text>
            </template>

            <template #panels>
                <contact-card-panel
                    :contact="contact"
                    variant="inline"
                    name="contact"
                    with-variable-height
                    editable
                />
                <contact-card-panel
                    v-if="hasSecondaryContact && contact.secondaryContact"
                    :contact="contact.secondaryContact"
                    variant="inline"
                    name="existing-partner"
                    with-variable-height
                    editable
                />
            </template>
        </inline-tabs>

        <template #sections>
            <card-section area="card-footer" class="card-footer">
                <flex-container :gap="Size.xs" justify-content="center">
                    <svg-icon
                        :icon="IconLogin"
                        :size="IconSize.sm"
                        :color="Color.gray400"
                    />
                    <text-decorator :size="FontSize.sm" :color="Color.gray400">
                        Voor het eerst binnengekomen op: {{ formatDate(contact.createdAt) }}
                    </text-decorator>
                </flex-container>
            </card-section>
        </template>
    </card-normal>
</template>

<script lang="ts" setup>
    import IconLogin from '@keyvue/one-material-icons/icons/login.svg?component';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import {
        TextDecorator,
        Color,
        Size,
        ButtonOutline,
        ButtonText,
        CardNormal,
        CardSection,
        FlexContainer,
        IconSize,
        InlineTabLabel,
        InlineTabs,
        SvgIcon,
        TextHeading,
        FontSize,
    } from '@keyvue/one-ui';

    import { useRouter } from 'vue-router';
    import { Contact } from '@keyvue/one-api-client';
    import { ref, computed, onMounted, provide, reactive } from 'vue';
    import ContactCardPanel from '@components/Tabs/Panels/ContactCardPanel.vue';
    import { formatDate } from '@/utils/date';
    import useBreadcrumb from '@composables/useBreadcrumb';
    import useClient from '@composables/useClient';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        contactId: string;
    }

    const props = defineProps<Props>();

    const eventBus = useEventBus();
    const router = useRouter();

    const { setBreadcrumb } = useBreadcrumb();
    const { client } = useClient();

    const contact = reactive(await client.get(Contact, props.contactId)) as Contact;
    provide('contact', contact);

    const reloadContact = async () => {
        Object.assign(contact, await client.get(Contact, props.contactId));
        updateBreadcrumb();
    };

    provide('reloadContact', reloadContact);

    const updateBreadcrumb = () => {
        setBreadcrumb([
            { title: 'Contacten', route: { name: 'contactList' } },
            { title: contact.name?.fullName ?? 'Onbekend', route: { name: 'contactDetail', params: { contactId: props.contactId } } },
        ]);
    };

    const contactTab = ref('contact');
    const openContactEditModal = () => {
        router.push({
            name: 'contactEdit',
            params: {
                contactId: props.contactId,
            },
            query: {
                contact: contactTab.value,
            },
        });
    };

    const hasSecondaryContact = computed(() => !!contact.secondaryContact);

    onMounted(async () => {
        updateBreadcrumb();
    });

    eventBus.subscribe<string>('resource-manipulated', (params) => {
        if (params === 'Contact') {
            Promise.resolve(reloadContact());
        }
    });
</script>

<style>
@media screen and (min-width: 1053px) and (max-width: 1150px) {
    .contact-card-partner--responsive {
        display: none;
    }
}
</style>
